import type { BookedFlightItinerary } from "@b2bportal/air-booking-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  ScheduleChangeModalState,
  type ScheduleChangeDiffModel,
} from "@b2bportal/core-types";
import {
  FlightScheduleChangeDetails,
  FlightScheduleChangeProcessing,
  FlightScheduleChangeAcceptSuccess,
  useFlightScheduleChangeModal,
  FlightScheduleChangeContactAirline,
  FlightScheduleChangeContactSupport,
} from "@tenant/flights";
import clsx from "clsx";

import defaultStyles from "./FlightScheduleChangeModalContent.module.scss";

export interface IFlightScheduleChangeModalContentProps {
  closeModal: () => void;
  hoursToDeparture?: number;
  itinerary: BookedFlightItinerary;
  scheduleChangeDiffs: ScheduleChangeDiffModel;
}

export const FlightScheduleChangeModalContent = (
  props: IFlightScheduleChangeModalContentProps
) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightScheduleChangeModalContent;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const scheduleChangeDataModel = useFlightScheduleChangeModal(props);
  const { context, text } = scheduleChangeDataModel;

  return (
    <div className={clsx(block)} onClick={(e) => e.stopPropagation()}>
      {context.modalState === ScheduleChangeModalState.acceptSuccess && (
        <FlightScheduleChangeAcceptSuccess
          classNames={{
            contentBody: cn("content-body"),
            contentHeader: cn("content-header"),
            contentSubtitle: cn("content-subtitle"),
            contentTitle: cn("content-title"),
            doneBtn: cn("done-btn", ["success"]),
            root: cn("schedule-change-accept-success"),
          }}
          scheduleChangeDataModel={scheduleChangeDataModel}
        />
      )}
      {context.modalState === ScheduleChangeModalState.contactAirline && (
        <FlightScheduleChangeContactAirline
          classNames={{
            airlineBookingDetails: cn("airline-booking-details"),
            airlineLocator: cn("airline-locator"),
            airlineLocatorLabel: cn("airline-locator-label"),
            closeBtn: cn("close-btn"),
            contentActions: cn("content-actions"),
            contentBody: cn("content-body"),
            contentHeader: cn("content-header"),
            contentSubtitle: cn("content-subtitle"),
            contentTitle: cn("content-title"),
            root: cn("schedule-change-contact-airline"),
            successImage: cn("success-image"),
            visitAirlineBtn: cn("visit-airline-btn"),
          }}
          scheduleChangeDataModel={scheduleChangeDataModel}
        />
      )}
      {context.modalState === ScheduleChangeModalState.details && (
        <FlightScheduleChangeDetails
          classNames={{
            contentBody: cn("content-body"),
            contentHeader: cn("content-header"),
            contentSubtitle: cn("content-subtitle"),
            contentTitle: cn("content-title"),
            originalSlice: cn("slice", ["original"]),
            originalSliceTitle: cn("slice-title", ["original"]),
            root: cn("schedule-change-details"),
            sliceComparator: cn("slice-comparator"),
            updatedSlice: cn("slice", ["updated"]),
            updatedSliceTitle: cn("slice-title", ["updated"]),
          }}
          hoursToDeparture={props.hoursToDeparture}
          itinerary={props.itinerary}
          scheduleChangeDataModel={scheduleChangeDataModel}
          scheduleChangeDiffs={props.scheduleChangeDiffs}
        />
      )}
      {context.modalState === ScheduleChangeModalState.processing && (
        <FlightScheduleChangeProcessing
          classNames={{
            contentBody: cn("content-body"),
            processingMessage: cn("content-subtitle"),
            processingSpinner: cn("processing-spinner"),
            root: cn("schedule-change-processing"),
          }}
          text={text}
        />
      )}
      {context.modalState === ScheduleChangeModalState.contactSupport && (
        <FlightScheduleChangeContactSupport
          classNames={{
            contentBody: cn("content-body"),
            title: cn("content-header"),
            subtitle: cn("content-subtitle"),
            root: cn("schedule-change-processing"),
          }}
          supportId={context.supportId}
          text={text}
        />
      )}
    </div>
  );
};
